import { useCallback, useEffect, useRef, useState } from "react";
import { isAborted, errorMessage } from "../../lib/apiError";

/**
 * Select cuộn-để-tải-thêm: mỗi lần cuộn tới đáy thì nới `pageSize` thêm 10 và nạp lại
 * từ trang 1 (BE không có cursor nên đây là cách rẻ nhất để danh sách dài ra).
 *
 * Trước đây hook chỉ `dispatch` thunk để nuôi slice `customer`, còn component tự đọc
 * `state.persistedReducer.customer.listCustomer` — nên hai màn dùng cùng lúc sẽ ghi đè
 * dữ liệu của nhau (cùng một slice, hai bộ tham số khác nhau). Nay hook tự giữ dữ liệu
 * nên mỗi chỗ dùng là một danh sách riêng.
 *
 * @param {(params: {currentPage: number, pageSize: number, value?: string}, signal?: AbortSignal) => Promise<any>} fetcher
 * @param {number} initialPageSize
 * @returns {{ items: any[], total: number, loading: boolean, hasMoreData: boolean,
 *             handlePopupScroll: Function, pageSizeScroll: number,
 *             search: (value: string) => void, reload: () => void }}
 */
const usePaginationSelect = (fetcher, initialPageSize = 10) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [pageSizeScroll, setPageSizeScroll] = useState(initialPageSize);
  const [value, setValue] = useState("");
  const [nonce, setNonce] = useState(0);

  // Nơi gọi thường truyền arrow function mới mỗi render; để nó vào deps là fetch vô hạn.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setLoading(true);
    fetcherRef
      .current({ currentPage: 1, pageSize: pageSizeScroll, value }, controller.signal)
      .then((res) => {
        if (!active) return;
        const rows = res?.data || [];
        setItems(rows);
        setTotal(res?.total ?? rows.length);
        // Trả về ít hơn số xin nghĩa là đã hết dữ liệu -> đừng xin thêm.
        setHasMoreData(rows.length >= pageSizeScroll);
      })
      .catch((e) => {
        if (!active || isAborted(e)) return;
        // Không chặn UI vì đây là dropdown phụ trợ; log để còn lần được nguyên nhân.
        console.warn("usePaginationSelect:", errorMessage(e));
        setHasMoreData(false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [pageSizeScroll, value, nonce]);

  const THRESHOLD = 10; // ngưỡng px coi là "đã tới đáy"

  const handlePopupScroll = (e) => {
    const { target } = e;
    const isAtBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + THRESHOLD;
    if (isAtBottom && !loading && hasMoreData) {
      setPageSizeScroll((n) => n + 10);
    }
  };

  /** Tìm trong dropdown: đổi từ khóa thì thu `pageSize` về ban đầu. */
  const search = useCallback(
    (next) => {
      setValue(next || "");
      setPageSizeScroll(initialPageSize);
      setHasMoreData(true);
    },
    [initialPageSize]
  );

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    items,
    total,
    loading,
    hasMoreData,
    handlePopupScroll,
    pageSizeScroll,
    search,
    reload,
  };
};

export default usePaginationSelect;
